import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaGoogle } from "react-icons/fa";
import { FiEye, FiEyeOff, FiUser, FiLock, FiMail } from "react-icons/fi";
import { Link } from "react-router-dom";
import styles from "./LoginDark.module.css";
import { AxiosError } from "axios";
import { useAuth } from "../contexts/AuthContext";
import authService from "../services/authService";
import { apiService } from "../services/apiService";
import InlineSpinner from "../components/common/InlineSpinner";
import { MdCheckCircle } from "react-icons/md";

const GoogleIcon = FaGoogle as React.FC<React.SVGProps<SVGSVGElement>>;
const EyeIcon = FiEye as React.FC<React.SVGProps<SVGSVGElement>>;
const EyeOffIcon = FiEyeOff as React.FC<React.SVGProps<SVGSVGElement>>;
const UserIcon = FiUser as React.FC<React.SVGProps<SVGSVGElement>>;
const LockIcon = FiLock as React.FC<React.SVGProps<SVGSVGElement>>;
const MailIcon = FiMail as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = MdCheckCircle as React.FC<React.SVGProps<SVGSVGElement>>;

const VCSignup: React.FC = () => {
  const {
    signInWithGoogle,
    requestEmailSignupOtp,
    error,
    confirmationEmailSent,
    user,
  } = useAuth();

  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [emailLoading, setEmailLoading] = useState<boolean>(false);
  const [registrationSuccess, setRegistrationSuccess] =
    useState<boolean>(false);
  const [googleLoading, setGoogleLoading] = useState<boolean>(false);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteValid, setInviteValid] = useState<boolean | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState<boolean>(true);
  const [inviteId, setInviteId] = useState<string | null>(null);
  const [processingInvite, setProcessingInvite] = useState<boolean>(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Resend confirmation email state
  const [resendTimer, setResendTimer] = useState<number>(10);
  const [canResend, setCanResend] = useState<boolean>(false);
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [resendSuccess, setResendSuccess] = useState<boolean>(false);
  const [resendError, setResendError] = useState<string | null>(null);

  // Check for invite token on mount
  useEffect(() => {
    const token = searchParams.get("invite");
    const errorType = searchParams.get("error");
    const expectedEmail = searchParams.get("expected");
    const gotEmail = searchParams.get("got");

    // Handle OAuth error from AuthContext redirect
    if (errorType === "email_mismatch" && expectedEmail && gotEmail) {
      setOauthError(
        `Email mismatch: You signed in with ${decodeURIComponent(
          gotEmail
        )} but this invite is for ${decodeURIComponent(
          expectedEmail
        )}. Please try again with the correct Google account.`
      );
    }

    if (token) {
      setInviteToken(token);
      validateInviteToken(token);
    } else {
      setInviteLoading(false);
      setInviteValid(false);
    }
  }, [searchParams]);

  // Handle invite acceptance after Google OAuth
  useEffect(() => {
    const acceptInviteAfterOAuth = async () => {
      // Check if we just returned from OAuth
      const pendingInviteToken = localStorage.getItem(
        "vc_pending_invite_token"
      );
      const pendingInviteId = localStorage.getItem("vc_pending_invite_id");
      const pendingInviteEmail = localStorage.getItem(
        "vc_pending_invite_email"
      );

      if (
        user &&
        pendingInviteToken &&
        pendingInviteId &&
        pendingInviteEmail &&
        !processingInvite
      ) {
        console.log("Processing invite after OAuth...");

        // CRITICAL: Verify the authenticated user's email matches the invited email
        if (user.email?.toLowerCase() !== pendingInviteEmail.toLowerCase()) {
          // Log detailed error only in non-production
          if (process.env.NODE_ENV !== "production") {
            console.error(
              "Email mismatch! OAuth email:",
              user.email,
              "Invited email:",
              pendingInviteEmail
            );
          } else {
            console.error(
              "Email mismatch between OAuth user and pending invite"
            );
          }

          // Sign out the user immediately
          await authService.signOut();

          // Clear localStorage
          localStorage.removeItem("vc_pending_invite_token");
          localStorage.removeItem("vc_pending_invite_id");
          localStorage.removeItem("vc_pending_invite_email");

          // Set error message instead of alert
          setOauthError(
            `Email mismatch: You signed in with ${user.email} but this invite is for ${pendingInviteEmail}. Please try again with the correct account.`
          );

          // Don't reload - let user try again
          return;
        }

        setProcessingInvite(true);
        try {
          await apiService.acceptInvite(pendingInviteId);

          // Clear localStorage
          localStorage.removeItem("vc_pending_invite_token");
          localStorage.removeItem("vc_pending_invite_id");
          localStorage.removeItem("vc_pending_invite_email");

          setRegistrationSuccess(true);
          setTimeout(() => {
            navigate("/onboarding/vc");
          }, 1500);
        } catch (err) {
          console.error("Failed to accept invite after OAuth:", err);

          // Set error message instead of alert
          setOauthError(
            "Failed to process your invitation. Please try again or contact support."
          );

          // Clear localStorage and sign out
          localStorage.removeItem("vc_pending_invite_token");
          localStorage.removeItem("vc_pending_invite_id");
          localStorage.removeItem("vc_pending_invite_email");
          await authService.signOut();
        }
      }
    };

    acceptInviteAfterOAuth();
  }, [user, processingInvite, navigate]);

  // Start countdown timer when confirmation email is sent
  useEffect(() => {
    if (confirmationEmailSent) {
      setCanResend(false);
      setResendTimer(10);

      const interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [confirmationEmailSent]);

  const validateInviteToken = async (token: string) => {
    setInviteLoading(true);
    try {
      const result = await apiService.validateInvite(token);
      if (result.valid && result.email) {
        setInviteValid(true);
        setEmail(result.email);
        setInviteId(result.inviteId || null);
      } else {
        setInviteValid(false);
        setInviteError(result.error || "Invalid invite");
      }
    } catch (err) {
      console.error("Error validating invite:", err);
      setInviteValid(false);
      setInviteError("Failed to validate invite");
    } finally {
      setInviteLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    // Clear any previous OAuth errors
    setOauthError(null);

    // Store invite details in localStorage before OAuth redirect
    // This persists through the OAuth flow
    if (inviteToken && inviteId && email) {
      localStorage.setItem("vc_pending_invite_token", inviteToken);
      localStorage.setItem("vc_pending_invite_id", inviteId);
      localStorage.setItem("vc_pending_invite_email", email);

      // Log sensitive details only in non-production
      if (process.env.NODE_ENV !== "production") {
        console.log("Stored invite details before OAuth:", {
          token: inviteToken,
          id: inviteId,
          email: email,
        });
      } else {
        console.log("Stored invite details before OAuth");
      }
    }

    setGoogleLoading(true);
    try {
      await signInWithGoogle();
      // User will be redirected to OAuth, then back
      // The useEffect will handle invite acceptance after return
    } catch (err) {
      console.error(err);
      setOauthError("Failed to initiate Google sign-in. Please try again.");
      // Clear localStorage on error
      localStorage.removeItem("vc_pending_invite_token");
      localStorage.removeItem("vc_pending_invite_id");
      localStorage.removeItem("vc_pending_invite_email");
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Block signup if no valid invite
    if (!inviteValid || !inviteId) {
      return;
    }

    // Password is required
    if (!password || password.length < 8) {
      return; // Form validation will show error
    }

    setEmailLoading(true);
    try {
      console.log("🔐 VC Email Signup - Starting registration...");

      // Register user via backend (auto-confirms email, sets VC role)
      const response = await apiService.post("/api/vc-signup/register", {
        email,
        password,
        inviteId,
      });

      if (response.data.success) {
        console.log("✅ VC user created successfully, signing in...");

        // Now sign in the user with their credentials
        await authService.login({ email, password });

        setRegistrationSuccess(true);
        // Redirect to VC onboarding after successful login
        setTimeout(() => {
          navigate("/onboarding/vc");
        }, 1500);
      } else {
        throw new Error(response.data.error || "Registration failed");
      }
    } catch (err) {
      console.error("❌ VC signup error:", err);
      // Extract the actual error message from Axios error response
      let errorMessage = "Registration failed. Please try again.";
      if (err instanceof AxiosError && err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setOauthError(errorMessage);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    setResendError(null);

    try {
      // For VCSignup, use the OTP method from AuthContext
      await requestEmailSignupOtp(email);

      setResendSuccess(true);
      // Reset timer
      setCanResend(false);
      setResendTimer(10);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setResendSuccess(false);
      }, 3000);
    } catch (err) {
      console.error("Error resending confirmation:", err);
      setResendError(
        err instanceof Error
          ? err.message
          : "Failed to resend confirmation email"
      );
      // Clear error after 5 seconds
      setTimeout(() => {
        setResendError(null);
      }, 5000);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.glowDots} aria-hidden="true">
        <div
          className={`${styles.dot} ${styles.dotBlue}`}
          style={{ top: "8%", left: "12%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotPurple}`}
          style={{ top: "20%", right: "10%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotYellow}`}
          style={{ bottom: "18%", left: "22%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotRed}`}
          style={{ bottom: "10%", right: "18%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotTeal}`}
          style={{ top: "40%", left: "8%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotPink}`}
          style={{ top: "65%", right: "8%" }}
        />
      </div>

      <div className={styles.container}>
        <div className={styles.logoSection}>
          <h1 className={styles.logoText}>
            SendUpdates<span className={styles.logoHighlight}>.ai</span>
          </h1>
          <p className={styles.subtitle}>VC Portal Sign Up</p>
        </div>

        {registrationSuccess ? (
          <div className={styles.card}>
            <div className={styles.successMessage}>
              <div className={styles.successIcon}>✓</div>
              <h2 className={styles.successTitle}>Registration Successful!</h2>
              <p className={styles.successText}>
                Redirecting you to setup your VC portal...
              </p>
            </div>
          </div>
        ) : confirmationEmailSent ? (
          <div className={styles.card}>
            <div className={styles.successMessage}>
              <div className={styles.successIcon}>📧</div>
              <h2 className={styles.successTitle}>Check Your Email</h2>
              <p className={styles.successText}>
                We sent a verification link to <strong>{email}</strong>. Click
                the link to complete your registration and access the VC portal.
              </p>

              {/* Resend button */}
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={!canResend || resendLoading}
                className={styles.resendButton}
                aria-label={
                  canResend
                    ? "Resend confirmation email"
                    : `Resend available in ${resendTimer} seconds`
                }
              >
                {resendLoading ? (
                  <>
                    <InlineSpinner />
                    Sending...
                  </>
                ) : (
                  <>
                    <MailIcon style={{ fontSize: "1rem" }} />
                    {canResend
                      ? "Resend Confirmation Email"
                      : `Resend in ${resendTimer}s`}
                  </>
                )}
              </button>

              {/* Success/Error feedback */}
              {resendSuccess && (
                <div className={styles.resendSuccess}>
                  <CheckIcon style={{ fontSize: "1rem" }} />
                  Email sent successfully!
                </div>
              )}
              {resendError && (
                <div className={styles.resendError}>{resendError}</div>
              )}
            </div>
          </div>
        ) : inviteLoading ? (
          <div className={styles.card}>
            <div className={styles.successMessage}>
              <InlineSpinner />
              <p className={styles.successText}>Validating invitation...</p>
            </div>
          </div>
        ) : !inviteValid ? (
          <div className={styles.card}>
            <div
              className={styles.errorBanner}
              style={{ marginBottom: "1.5rem" }}
            >
              <span className={styles.errorIcon}>🔒</span>
              <div>
                <strong>Invitation Required</strong>
                <p style={{ marginTop: "0.5rem", fontSize: "0.95rem" }}>
                  {inviteError || "VC Portal signup is by invitation only."}
                </p>
              </div>
            </div>

            <h2 className={styles.title}>Access Restricted</h2>
            <p className={styles.description}>
              The VC Portal is available by invitation only. If you&apos;re
              interested in joining, please contact us.
            </p>

            <div style={{ marginTop: "2rem", textAlign: "center" }}>
              <Link
                to="/login"
                className={styles.link}
                style={{ fontSize: "1rem" }}
              >
                Log In
              </Link>
            </div>
          </div>
        ) : (
          <div className={styles.card}>
            <h2 className={styles.title}>Accept Your Invite</h2>
            <p className={styles.description}>
              Get started with portfolio company insights and automated updates
            </p>

            {(error || oauthError) && (
              <div className={styles.errorBanner}>
                <span className={styles.errorIcon}>⚠️</span>
                <div>{oauthError || error}</div>
              </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.inputGroup}>
                <div className={styles.inputIcon}>
                  <UserIcon />
                </div>
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={true}
                  readOnly
                  className={styles.input}
                  style={{
                    backgroundColor: "#1a1a2e",
                    cursor: "not-allowed",
                    paddingLeft: "45px",
                  }}
                />
              </div>

              <div className={styles.inputGroup}>
                <div className={styles.inputIcon}>
                  <LockIcon />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password (minimum 8 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={emailLoading || googleLoading}
                  required
                  minLength={8}
                  className={styles.input}
                  style={{ paddingLeft: "45px", paddingRight: "45px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.eyeButton}
                  disabled={emailLoading || googleLoading}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>

              <button
                type="submit"
                disabled={emailLoading || googleLoading}
                className={styles.submitButton}
              >
                {emailLoading ? <InlineSpinner /> : "Create VC Account"}
              </button>
            </form>

            {/* <button
              type="button"
              onClick={handleGoogleSignup}
              disabled={googleLoading || emailLoading}
              className={styles.socialButton}
            >
              {googleLoading ? (
                <InlineSpinner />
              ) : (
                <GoogleIcon className={styles.socialIcon} />
              )}
              <span>Continue with Google</span>
            </button> */}

            {/* <p
              className={styles.hint}
              style={{
                marginTop: "1rem",
                fontSize: "0.75rem",
                color: "rgba(255, 255, 255, 0.6)",
              }}
            >
              Note: If using Google, you must sign in with{" "}
              <strong style={{ color: "rgba(255, 255, 255, 0.9)" }}>
                {email}
              </strong>
            </p> */}

            <div className={styles.footer}>
              Already have an account?{" "}
              <Link to="/login" className={styles.link}>
                Sign In
              </Link>
            </div>

            <div className={styles.terms}>
              By signing up, you agree to our{" "}
              <Link to="/terms" className={styles.link}>
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className={styles.link}>
                Privacy Policy
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VCSignup;
